// Load matching tab (ST-00218 P2): small shared pieces — the tier palette and tier-mix bar,
// the data-quality and sign-off badges, hint text with `code` spans, the amend link that
// hands a ticket to the Amendments page, and the "switched off" state.
import { Fragment, useContext, type Context, type ReactNode } from 'react';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaEye,
  FaExternalLinkAlt,
  FaPowerOff,
} from 'react-icons/fa';
import TabContextJs from '../../../../context/TabContext';
import { useTheme } from '../../../../context/ThemeContext';
import type { AmendLink, DqSummary, RouteSignoffState, Tier } from '../../../../types/loadMatching';
import { DQ_INDICATOR_LABELS, SIGNOFF_STATE_LABELS, TIER_LABELS, TIERS, fmtN } from './format';

// ───────────────────────────────────────────────────────────── tier palette ──
//
// Tiers are ordinal (1 = most certain … 4 = unconfirmed), so they take ONE hue (the brand teal)
// stepped light → dark, not categorical colours. Validated with the dataviz skill's
// validate_palette.js --ordinal: light on #ffffff (light end 2.07:1), dark on #161b22
// (light end 3.55:1); monotone lightness, ΔL ≥ 0.06 between steps, single hue. In dark mode
// the most certain tier is the brightest step.
const TIER_COLORS_LIGHT: Record<Tier, string> = {
  1: '#1c524c',
  2: '#2b7a71',
  3: '#3fa498',
  4: '#6cc3b9',
};
const TIER_COLORS_DARK: Record<Tier, string> = {
  1: '#c3f0ea',
  2: '#7fd8cd',
  3: '#45b7aa',
  4: '#2f7d74',
};

/** The tier colours for the active theme (re-renders when the theme flips). */
export function useTierColors(): Record<Tier, string> {
  const { resolvedTheme } = useTheme() as { resolvedTheme?: string };
  return resolvedTheme === 'dark' ? TIER_COLORS_DARK : TIER_COLORS_LIGHT;
}

type TierCounts = { t1: number; t2: number; t3: number; t4: number };
const countOf = (m: TierCounts, tier: Tier): number => m[`t${tier}` as keyof TierCounts];

/** Hover / screen-reader text of a tier mix: every tier with its count and share. */
export function tierMixText(mix: TierCounts): string {
  const total = TIERS.reduce((s, t) => s + countOf(mix, t), 0);
  if (!total) return 'No matched loads';
  return TIERS.map((t) => {
    const n = countOf(mix, t);
    const share = (n / total) * 100;
    return `${TIER_LABELS[t].short}: ${fmtN(n)} (${share < 0.1 && n > 0 ? '<0.1' : share.toFixed(1)}%)`;
  }).join(' · ');
}

/**
 * How the matched loads were matched, as one stacked bar drawn to scale (flex-grow = count);
 * a tier that has loads keeps a 2 px sliver so it is never invisible. 2 px surface gaps
 * separate the segments; the outer ends are rounded.
 */
export function TierMixBar({
  mix,
  className = '',
  height = 8,
}: {
  mix: TierCounts;
  className?: string;
  height?: number;
}) {
  const colors = useTierColors();
  const total = TIERS.reduce((s, t) => s + countOf(mix, t), 0);
  const text = tierMixText(mix);
  if (!total) {
    return (
      <div
        className={`rounded bg-slate-100 ${className}`}
        style={{ height }}
        role="img"
        aria-label={text}
        title={text}
      />
    );
  }
  return (
    <div
      className={`flex gap-[2px] rounded overflow-hidden ${className}`}
      style={{ height }}
      role="img"
      aria-label={`Matched by: ${text}`}
      title={text}
    >
      {TIERS.filter((t) => countOf(mix, t) > 0).map((t) => (
        <span
          key={t}
          data-tier={t}
          style={{
            flexGrow: countOf(mix, t),
            flexBasis: 0,
            minWidth: 2,
            background: colors[t],
          }}
        />
      ))}
    </div>
  );
}

/** The tier legend: swatch + name + count (identity never by colour alone). */
export function TierLegend({ mix, long = false }: { mix?: TierCounts; long?: boolean }) {
  const colors = useTierColors();
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-600">
      {TIERS.map((t) => (
        <li key={t} className="inline-flex items-center gap-1.5">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
            style={{ background: colors[t] }}
            aria-hidden="true"
          />
          <span>
            {t} · {long ? TIER_LABELS[t].long : TIER_LABELS[t].short}
            {mix && (
              <span className="ml-1 tabular-nums text-slate-500">{fmtN(countOf(mix, t))}</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ─────────────────────────────────────────────────────────────── badges ──

/** Data-quality indicator: icon + label, status colour never alone. */
export function DqBadge({ indicator }: { indicator: DqSummary['indicator'] }) {
  const label = DQ_INDICATOR_LABELS[indicator];
  if (indicator === 'ok') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700">
        <FaCheckCircle className="w-3 h-3" aria-hidden="true" /> {label}
      </span>
    );
  }
  if (indicator === 'review') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700">
        <FaEye className="w-3 h-3" aria-hidden="true" /> {label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700">
      <FaExclamationTriangle className="w-3 h-3" aria-hidden="true" /> {label}
    </span>
  );
}

const SIGNOFF_TONES: Record<RouteSignoffState['state'], string> = {
  none: 'bg-slate-100 text-slate-600 border-slate-200',
  signed: 'bg-sky-50 text-sky-800 border-sky-200',
  switched: 'bg-primary-50 text-primary-800 border-primary-200',
  reverted: 'bg-amber-50 text-amber-800 border-amber-200',
};

export function SignoffBadge({ state }: { state: RouteSignoffState['state'] }) {
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-semibold whitespace-nowrap ${SIGNOFF_TONES[state]}`}
    >
      {SIGNOFF_STATE_LABELS[state]}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────── text ──

/** Hint text from the API with `LOCATION` spans rendered as code chips. */
export function HintText({ text }: { text: string }) {
  const parts = text.split('`');
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <code
            key={i}
            className="px-1 py-px rounded bg-slate-100 text-slate-800 font-mono text-[0.95em]"
          >
            {p}
          </code>
        ) : (
          <Fragment key={i}>{p}</Fragment>
        )
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────── amend link ──

/** TabContext.js is untyped (createContext(null)); the tab only opens tabs. */
const TabContext = TabContextJs as unknown as Context<{
  openTab: (url: string, options?: { background?: boolean }) => void;
} | null>;

/**
 * A link that opens an app page in a new in-app tab (with the tab strip) or else a new browser
 * tab, so the review it came from stays where it is. `srHint` names the page for screen readers.
 */
export function TabLink({
  href,
  srHint,
  children,
  ariaLabel,
}: {
  href: string;
  srHint: string;
  children: ReactNode;
  ariaLabel?: string;
}) {
  const tabs = useContext(TabContext);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      onClick={(e) => {
        if (tabs) {
          e.preventDefault();
          tabs.openTab(href);
        }
      }}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-700 hover:text-primary-900 underline-offset-2 hover:underline"
    >
      {children}
      <FaExternalLinkAlt className="w-2.5 h-2.5 flex-shrink-0" aria-hidden="true" />
      <span className="sr-only"> ({srHint})</span>
    </a>
  );
}

/**
 * Opens the Amendments page filtered to the ticket (`?ticketUIDs=<uid>&field=…&value=…`, the
 * existing hand-off). With the in-app tab strip it opens a tab, so the review stays where it
 * is; without it a new browser tab. Users who cannot open Amendments see the suggestion only.
 */
export function AmendLinkButton({ link, canAmend }: { link: AmendLink; canAmend: boolean }) {
  if (!canAmend) {
    return <span className="text-[11px] text-slate-500">{link.label}</span>;
  }
  return (
    <TabLink href={link.href} srHint="opens Amendments">
      {link.label}
    </TabLink>
  );
}

// ─────────────────────────────────────────────────────────── off state ──

export function SwitchedOffState({ children }: { children?: ReactNode }) {
  return (
    <div
      role="status"
      className="bg-surface rounded-lg border border-slate-200 px-6 py-12 text-center"
    >
      <FaPowerOff className="w-8 h-8 mx-auto mb-3 text-slate-300" aria-hidden="true" />
      <h3 className="text-sm font-semibold text-slate-800 mb-1">
        Load matching is switched off on this server
      </h3>
      <p className="text-xs text-slate-500 max-w-md mx-auto">
        {children ||
          'Your reports keep using the current Brought Forward / Carried Forward matching. An administrator can switch load matching on with the LOAD_MATCHING server setting.'}
      </p>
    </div>
  );
}
